import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState, useSetRecoilState } from "recoil";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import {
  addStateDataConnection,
  editStateDataConnection,
  rowEditDataConnection,
  dataConnectionFetching,
} from "../atoms/atoms";
import { IDataCon } from "../types";
import useEditDataConnection from "../hooks/useEditDataConnection";
import useAddDataConnection from "../hooks/useAddDataConnection";
import SubmitHookForm from "components/SubmitHookForm";
import DataConnectionDetails from "./DataConnectionDetails";

export const DataConnectionForm = (props: {
  setRecent: React.Dispatch<React.SetStateAction<string>>;
}) => {
  // Drilled Props
  const { setRecent } = props;

  // Atom States
  const [isAddState, setIsAddState] = useRecoilState(addStateDataConnection);
  const [actionEditState, setActionEditState] = useRecoilState(
    editStateDataConnection
  );
  const [editData, setEditData] = useRecoilState(rowEditDataConnection);
  const setIsFetching = useSetRecoilState(dataConnectionFetching);

  // Local States
  const [newData, setNewData] = useState<IDataCon>();
  const [saveModal, setSaveModal] = useState(false);
  const [changes, setChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Hook Functions
  const updateData = useEditDataConnection();
  const addData = useAddDataConnection();

  // Form Functions
  const useFormMethods = useForm({
    defaultValues: { ...editData },
  });

  const onSubmit = (data) => {
    const value = {
      ...data,
      dataConnectionName: data.dataConnectionName.trim(),
    };
    setNewData(value);
    setSaveModal(true);
    setChanges(false);
    setIsSaving(true);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setActionEditState(false);
  };

  const handleSave = async () => {
    if (newData) {
      if (actionEditState) {
        updateData(newData);
      } else {
        addData(newData);
      }
    }

    setRecent("");
    setIsFetching(true);
    setIsSaving(false);
    setEditData(undefined);
    useFormMethods.reset();
    useFormMethods.clearErrors();
  };

  return (
    <>
      <div className="p-2 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
              <div className="ml-2 font-bold text-primary text-base">
                {isAddState
                  ? "Add New Data Connection"
                  : "Edit Data Connection"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="submit"
                  disabled={!changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="mx-2 border-b-2 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <DataConnectionDetails data={editData} setChanges={setChanges} />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={
            isAddState ? "Add Data Connection" : "Update Data Connection"
          }
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
            setChanges(true);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            handleSave();
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};

export default DataConnectionForm;
